import React, { useState, useEffect } from 'react';
import { PageHeader } from '../ui';
import { adminAPI } from '../services/api';

const AdminPanel = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState({});

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 20,
        total: 0,
        totalPages: 0
    });

    // Filter state
    const [statusFilter, setStatusFilter] = useState('pending');

    useEffect(() => {
        loadUsers();
    }, [pagination.page, pagination.perPage, statusFilter]);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await adminAPI.getUsers();

            let allUsers = response.data || [];
            // Client-side status filter
            if (statusFilter) {
                allUsers = allUsers.filter(u => u.status === statusFilter);
            }

            setUsers(allUsers);
            setPagination(prev => ({
                ...prev,
                total: allUsers.length,
                totalPages: 1
            }));
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Не вдалося завантажити користувачів');
        } finally {
            setLoading(false);
        }
    };

    const handleUserAction = async (userId, status) => {
        setActionLoading(prev => ({ ...prev, [userId]: true }));

        try {
            await adminAPI.approveUser(userId, status);
            // Reload users after action
            await loadUsers();
        } catch (err) {
            console.error('Error updating user:', err);
            setError(status === 'approved' ? 'Не вдалося схвалити користувача' : 'Не вдалося відхилити користувача');
        } finally {
            setActionLoading(prev => ({ ...prev, [userId]: false }));
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePerPageChange = (event) => {
        const newPerPage = parseInt(event.target.value);
        setPagination(prev => ({
            ...prev,
            perPage: newPerPage,
            page: 1
        }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(new Date(dateString));
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'approved': return 'status-approved';
            case 'pending': return 'status-pending';
            case 'rejected': return 'status-rejected';
            default: return '';
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'role-admin';
            case 'user': return 'role-user';
            case 'guest': return 'role-guest';
            default: return '';
        }
    };

    return (
        <div className="ui-page">
            <PageHeader title="Адміністрування" description="Схвалення нових користувачів і керування доступом." />
            <div>

                <div className="ui-card ui-card--flush">
                    <div className="section-header">
                        <h2>Користувачі</h2>
                        <div className="filters">
                            <label htmlFor="status-filter">Статус</label>
                            <select
                                id="status-filter"
                                value={statusFilter || ''}
                                onChange={(e) => setStatusFilter(e.target.value || null)}
                            >
                                <option value="">Усі</option>
                                <option value="pending">Очікують</option>
                                <option value="approved">Схвалені</option>
                                <option value="rejected">Відхилені</option>
                            </select>
                        </div>
                    </div>

                    {error && (
                        <div className="error-message" style={{ margin: '0 24px 16px' }}>
                            {error}
                            <button onClick={loadUsers} className="retry-btn">
                                Повторити
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-container"><div className="spinner" /><p>Завантаження користувачів…</p></div>
                    ) : (
                        <>
                            {users.length > 0 ? (
                                <>
                                    <div className="users-count">
                                        Показано {users.length} з {pagination.total}
                                    </div>

                                    <div className="ui-table-wrap">
                                        <table className="ui-table users-table">
                                            <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th>Роль</th>
                                                    <th>Статус</th>
                                                    <th>Зареєстрований</th>
                                                    <th>Схвалений</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map(user => (
                                                    <tr key={user.user_id}>
                                                        <td className="user-email">{user.email}</td>
                                                        <td>
                                                            <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                                                                {ROLE_LABELS[user.role] || user.role}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                                                                {STATUS_LABELS[user.status] || user.status}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(user.created_at)}</td>
                                                        <td>{formatDate(user.approved_at)}</td>
                                                        <td>
                                                            <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                                                                {user.status === 'pending' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleUserAction(user.user_id, 'approved')}
                                                                            disabled={actionLoading[user.user_id]}
                                                                            className="approve-btn"
                                                                        >
                                                                            {actionLoading[user.user_id] ? 'Зачекай…' : 'Схвалити'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleUserAction(user.user_id, 'rejected')}
                                                                            disabled={actionLoading[user.user_id]}
                                                                            className="reject-btn"
                                                                        >
                                                                            {actionLoading[user.user_id] ? 'Зачекай…' : 'Відхилити'}
                                                                        </button>
                                                                    </>
                                                                )}

                                                                {user.status === 'approved' && (
                                                                    <button
                                                                        onClick={() => handleUserAction(user.user_id, 'rejected')}
                                                                        disabled={actionLoading[user.user_id]}
                                                                        className="reject-btn"
                                                                    >
                                                                        {actionLoading[user.user_id] ? 'Зачекай…' : 'Забрати доступ'}
                                                                    </button>
                                                                )}

                                                                {user.status === 'rejected' && (
                                                                    <button
                                                                        onClick={() => handleUserAction(user.user_id, 'approved')}
                                                                        disabled={actionLoading[user.user_id]}
                                                                        className="approve-btn"
                                                                    >
                                                                        {actionLoading[user.user_id] ? 'Зачекай…' : 'Схвалити'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {pagination.totalPages > 1 && (
                                        <div className="pagination">
                                            <div className="pagination-info">
                                                <select
                                                    value={pagination.perPage}
                                                    onChange={handlePerPageChange}
                                                    className="per-page-select"
                                                >
                                                    <option value={10}>10 на сторінці</option>
                                                    <option value={20}>20 на сторінці</option>
                                                    <option value={50}>50 на сторінці</option>
                                                </select>
                                            </div>

                                            <div className="pagination-controls">
                                                <button
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={pagination.page === 1}
                                                    className="page-btn"
                                                >
                                                    Перша
                                                </button>

                                                <button
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page === 1}
                                                    className="page-btn"
                                                >
                                                    Назад
                                                </button>

                                                <span className="page-info">
                                                    {pagination.page} з {pagination.totalPages}
                                                </span>

                                                <button
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.totalPages}
                                                    className="page-btn"
                                                >
                                                    Далі
                                                </button>

                                                <button
                                                    onClick={() => handlePageChange(pagination.totalPages)}
                                                    disabled={pagination.page === pagination.totalPages}
                                                    className="page-btn"
                                                >
                                                    Остання
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="no-users">
                                    Користувачів не знайдено.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const ROLE_LABELS = { admin: 'Адміністратор', user: 'Користувач', guest: 'Гість' };
const STATUS_LABELS = { pending: 'Очікує', approved: 'Схвалений', rejected: 'Відхилений' };

export default AdminPanel;
